/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   zrsrc
 */

import { logger } from '../../logger'
import * as Utils from '../../shared/utils/utils'

// { begin z-rsrc code (lifted from dora)

/**
 * a factory function that returns a "Resource Manager". the manager has a variety of helper
 * functions based on the provided 'actions'
 *
 * each configured action is added as a function to the rsrc manager. the signature depends on
 * if the http method is non-data or data-capable eg. get/delete vs post/put/patch etc.
 *
 * non-data: mgr.get({ params }[, { config }])
 * - some examples:
 * - listings.get({ minPrice: 1000, maxPrice: 1100, limit: 10})
 * - listings.get({ minPrice: 1000, maxPrice: 1100, limit: 10}, { headers: { csrf: 'eg' } })
 *
 * w/ -data: mgr.post({ body-data }[, { params }[, { config }]])
 * - some examples:
 * - eg: message.send({ message: 'great!', listing_ids: [1234] })
 * - eg: message.send({ message: 'great!', listing_ids: [1234] }, {}, { headers: { csrf: 'eg' } })
 * - eg: search.update({ search_id: 3, query: { } }, { other: 'search-page' })
 *
 * @param {Function} $fetch the fetch() factory. returns a Promise() for a transformed api response.
 * @param {string} url      the base url 'pattern' ala $resource, it may have ':variable' elements
 * @param {object} actions  a dictionary of actions. name to http method/config etc. ala $resource
 *                          functions named after the keys are bound onto the resource type returned
 * @param {object=} config  config
 * @param Bizo              a type/object-constructor.
 * @param {Promise | object} initializer a promise-ish. all fetches are inside promise.then()
 */
const zrsrc = ($fetch, url, actions, config, Bizo, initializer) => {
  // here we define the Manager. it will hold a few standard managment fuctions/attributes here, and
  // later we'll bind the actions onto it.
  const manager = {
    Bizo,
    url,
  }

  // '_pre' is a special action attribute that registers a 'preceeding' function over the params
  // '_pre' on the actions object (as opposed to a specific action's options) configures a default
  // for all actions of this manager
  const pre = actions ? actions._pre : undefined

  // register the default actions with the generic configuration
  if (!actions.post) {
    zrsrc.addDataActionMethod(manager, $fetch, ['post'], { _pre: pre }, initializer)
  }
  if (!actions.get) {
    zrsrc.addActionMethod(manager, $fetch, ['get'], { _pre: pre }, initializer)
  }

  // register actions, providing default _pre and registering w/ or w/o data based on http method
  if (actions) {
    Utils.oforEach(actions, (action, name) => {
      if (name[0] !== '_') {
        if (pre && !action._pre) {
          action._pre = pre
        }
        if (/^(POST|PUT|PATCH)$/i.test(action.method) || config?.addDataActionMethod) {
          zrsrc.addDataActionMethod(manager, $fetch, [name], action, initializer)
        } else {
          zrsrc.addActionMethod(manager, $fetch, [name], action, initializer)
        }
      }
    })
  }
  return manager
}

// zrsrc helper functions

/**
 * interpolate the params into the url. return url & unused params
 * eg. 'listings/:listingId' + {listingId: 7} = 'listings/7'
 *
 * @param {string} url
 * @param {object} params
 * @param {object} data
 * @returns {object}, {url: newUrl, params: unused}, unused is undefined if all are used
 */
zrsrc.fillUrl = (url, params = {}, data = undefined) => {
  // TODO(bz): encodeURI ?? i think we probably should..
  let u = url
  let used = {}
  let unused
  if (url.indexOf(':') >= 0) {
    u = url.replace(/:(\w+)/g, (m, slug) => {
      used[slug] = true
      if (params[slug] !== undefined) {
        return params[slug]
      } else if (data && data[slug] !== undefined) {
        return data[slug]
      }
      return ''
    })
    used = Object.keys(used)
    if (params && used.length !== Object.keys(params).length) {
      unused = { ...params }
      for (const param of used) {
        delete unused[param]
      }
    }
  } else {
    unused = params
  }
  return { url: u, params: unused }
}

/**
 * apply unused params to url for non-data action method.
 *
 * @param {object} args
 * @returns {object}, { url: newUrl, params: unused }
 */
zrsrc.applyUnusedParamsToUrl = (args = {}) => {
  const { params } = args
  let { url } = args
  if (params) {
    const attrs = Object.keys(params).sort()
    const attrsList = []
    if (attrs.length) {
      attrs.forEach((attr) => {
        const param = params[attr]
        if (param !== undefined) {
          const encodedParam = param === null ? '' : encodeURIComponent(param)
          attrsList.push(`${encodeURIComponent(attr)}=${encodedParam}`)
        }
      })
      if (attrsList.length) {
        url = `${url}?${attrsList.join('&')}`
      }
    }
  }
  return { url, params }
}

/**
 * adds a non-data action method. eg. ['get', 'list']
 *
 * @param manager
 * @param $fetch data promise factory
 * @param {Array<string>} names eg. ['get', 'list']
 * @param {object} action
 * @param {Promise} initializer
 */
zrsrc.addActionMethod = (manager, $fetch, names, action, initializer) => {
  const { Bizo, url } = manager

  for (const name of names) {
    const actn = action ? { ...action, name } : { name, method: name }
    const method = (actn.method || name).toLowerCase()

    manager[name] = (params, config) => {
      if (actn._pre) {
        actn._pre(params)
      }
      const args = zrsrc.applyUnusedParamsToUrl(zrsrc.fillUrl(url, params))
      const helper = () => {
        logger.debug('action helper:', name, method, args.url, args.params)
        return $fetch({
          ...config,
          ...action,
          Bizo,
          method,
          url: args.url.replace(/\/+$/, ''),
        })
      }
      // we expect that this is generally bundle, which acts like a Promise (has a then())
      return initializer ? initializer.then(helper, helper) : helper()
    }
  }
}

/**
 * adds a data action method. eg. ['post', 'put', 'send']
 *
 * @param manager
 * @param $fetch data promise factory
 * @param {Array<string>} names
 * @param {object} action
 * @param {Promise} initializer
 */
zrsrc.addDataActionMethod = (manager, $fetch, names, action, initializer) => {
  const { Bizo, url } = manager
  for (const name of names) {
    const actn = action ? { ...action, name } : { name, method: name }
    const method = (actn.method || name).toLowerCase()

    manager[name] = (data, params, config) => {
      // - Manager.action(data, params, config)
      if (actn._pre) {
        actn._pre(params, data)
      }
      // checking `instanceof Buffer` causes a 22k polyfill, we check for the object has some
      // indicative method adttributes: `data.writeUInt16BE`
      const body =
        data === undefined || data instanceof String || data.writeUInt16BE
          ? data
          : JSON.stringify(data, (key, value) => {
              // don't send __typename property to BE; it can be upset by it
              // https://sentryv2.zumper.com/organizations/zumper/issues/138808/?end=2023-06-05T19%3A40%3A59&project=432&query=firstRelease%3Ab49482e1ddd609b86b9147901600e2d2acb15ef8&referrer=issue-stream&sort=freq&start=2023-06-05T19%3A19%3A00
              if (key !== '__typename') {
                return value
              }
            })
      // use a bizo'd version of the input data as the default backup to params for url construction
      let bizo = data
      if (data && Bizo) {
        bizo = Bizo.fromJS ? Bizo.fromJS(data) : new Bizo(data)
      }
      const args = zrsrc.fillUrl(url, params, bizo)

      const helper = () => {
        return $fetch({
          ...config,
          ...action,
          Bizo,
          body,
          method,
          url: args.url.replace(/\/+$/, ''),
        })
      }

      // we expect that this is generally bundle, which acts like a Promise (has a then())
      return initializer ? initializer.then(helper, helper) : helper()
    }
  }
}

export default zrsrc
